import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/formatters';
import { apiGet } from '../../utils/apiClient';
import CurrencyInput, { formatWithSpaces } from '../common/CurrencyInput';
import PaymentMethodSelect from '../common/PaymentMethodSelect';
import ProofUploadField from '../common/ProofUploadField';
import { useTenant } from '../../contexts/TenantContext';

/**
 * Unified match dialog used by Payments / Combined / WHT flows.
 *
 * Sections rendered:
 *   - Header (always)
 *   - Payment metadata (always)
 *   - Apportionment — only when matchType === 'combined'
 *   - Variance — only when |payment_amount - expected_total| >= 0.01
 *
 * The dialog does NOT pick the endpoint. Callers receive a structured payload
 * via onSubmit and route to the right endpoint themselves.
 *
 *   matchType = 'payment'  → row narration is Interest Payment / Capital Payment / Adhoc Capital Withdrawal
 *   matchType = 'combined' → row narration is Combined Payment (interest + capital split)
 *   matchType = 'wht'      → WHT payment
 */
const MatchDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  matchType = 'payment',
  scheduleRow,
  currencyCode,
  contractReference,
  userReference,
  investorName,
  /* T117 multi-currency: when the contract settles in a different currency than it accrues
     in, the user enters the foreign settlement amount + dealt FX rate, and the contract-
     currency equivalent (= settlement * rate) is what feeds the existing variance/allocation
     logic. Pass settlementCurrencyCode (e.g. 'BWP') to enable; null/equal => legacy behaviour.
     currencyCode is the contract (accrual) currency. */
  settlementCurrencyCode,
  /* WHT-only extras shown in the header for context */
  whtContext, // { gross_interest, wht_rate, wht_amount } when matchType === 'wht'
  /* Mode: 'match' (default) creates a new match; 'reapportion' edits an
     existing match in place. In reapportion mode the cash amount and all
     payment metadata are locked read-only; only the allocation and variance
     reason are editable. */
  mode = 'match',
  initialPayment, // { payment_amount, payment_date, payment_reference, payment_method, notes } pre-fill
  initialAllocation, // { allocated_interest, allocated_capital } pre-fill
  initialVarianceReason, // string pre-fill
}) => {
  const { isWorkflowDocumentRequired } = useTenant();
  const workflowCode = matchType === 'wht' ? 'wht_match' : 'payment_match';
  const proofRequired = isWorkflowDocumentRequired(workflowCode);
  const proofLabelBase = matchType === 'wht' ? 'Proof of WHT Payment' : 'Proof of Payment';
  const proofLabel = `${proofLabelBase} ${proofRequired ? '(Required)' : '(Optional)'}`;

  const isReapportion = mode === 'reapportion';
  const isView = mode === 'view';
  // T117: multi-currency when a settlement currency is supplied and differs from the contract ccy.
  const isMultiCurrency = !!settlementCurrencyCode && settlementCurrencyCode !== currencyCode;
  // In view mode every input is read-only; in reapportion mode only the
  // cash/date/reference/method/notes/proof are locked but the allocation +
  // variance reason are editable.
  const lockMetadata = isReapportion || isView;
  const lockAllocation = isView;
  const expectedInterest = parseFloat(scheduleRow?.interest_amount) || 0;
  const expectedCapital = parseFloat(scheduleRow?.capital_amount) || 0;
  const expectedTotal = parseFloat(scheduleRow?.scheduled_amount)
    || parseFloat(scheduleRow?.payment_amount)
    || (expectedInterest + expectedCapital);

  // Form state
  const [paymentAmount, setPaymentAmount] = useState(expectedTotal);
  // Default the date to the scheduled payment date if present, else today.
  // Users typically capture cash on or near the scheduled date, so the
  // schedule date is a better starting point than 'now'.
  const initialDate = scheduleRow?.payment_date
    ? format(new Date(scheduleRow.payment_date), 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');
  const [paymentDate, setPaymentDate] = useState(initialDate);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('EFT');
  const [notes, setNotes] = useState('');
  const [proofFile, setProofFile] = useState(null);

  // Apportionment state (Combined only)
  const [allocatedInterest, setAllocatedInterest] = useState(expectedInterest);
  const [allocatedCapital, setAllocatedCapital] = useState(expectedCapital);

  // Variance reason
  const [varianceReason, setVarianceReason] = useState('');

  // T117 multi-currency state: the foreign cash actually moved + the dealt rate
  // (settlement -> contract). The contract-currency equivalent (paymentAmount) is derived.
  const [settlementAmount, setSettlementAmount] = useState(0);
  const [fxRate, setFxRate] = useState('');
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState('');

  // Reset state when the dialog opens with a new schedule row.
  // In reapportion mode, seed from initial* props (existing payment + allocation);
  // otherwise seed from the schedule row defaults.
  useEffect(() => {
    if (!open) return;
    const ei = parseFloat(scheduleRow?.interest_amount) || 0;
    const ec = parseFloat(scheduleRow?.capital_amount) || 0;
    const et = parseFloat(scheduleRow?.scheduled_amount)
      || parseFloat(scheduleRow?.payment_amount)
      || (ei + ec);

    if ((isReapportion || isView) && initialPayment) {
      setPaymentAmount(parseFloat(initialPayment.payment_amount) || 0);
      setPaymentDate(initialPayment.payment_date
        ? format(new Date(initialPayment.payment_date), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'));
      setPaymentReference(initialPayment.payment_reference || '');
      setPaymentMethod(initialPayment.payment_method || 'EFT');
      setNotes(initialPayment.notes || '');
      // T117: surface the dealt settlement amount + rate read-only on an existing match.
      setSettlementAmount(parseFloat(initialPayment.settlement_amount) || 0);
      setFxRate(initialPayment.fx_rate != null ? String(initialPayment.fx_rate) : '');
    } else {
      setPaymentAmount(isMultiCurrency ? 0 : et);
      setPaymentDate(scheduleRow?.payment_date
        ? format(new Date(scheduleRow.payment_date), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'));
      setPaymentReference('');
      setPaymentMethod('EFT');
      setNotes('');
      // T117: a fresh multi-currency match starts blank; the rate is auto-suggested by the
      // fetch effect, and T119 auto-fills the settlement amount from the schedule (÷ rate).
      setSettlementAmount(0);
      setFxRate('');
    }
    setProofFile(null);

    if (isReapportion && initialAllocation) {
      setAllocatedInterest(parseFloat(initialAllocation.allocated_interest) || 0);
      setAllocatedCapital(parseFloat(initialAllocation.allocated_capital) || 0);
    } else {
      setAllocatedInterest(ei);
      setAllocatedCapital(ec);
    }
    setVarianceReason(isReapportion ? (initialVarianceReason || '') : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleRow]);

  // When the user changes payment_amount on a Combined row, default the split
  // interest-first against the new cash amount. Skipped in reapportion mode
  // since the cash amount is locked there.
  useEffect(() => {
    if (matchType !== 'combined' || isReapportion) return;
    const cash = parseFloat(paymentAmount) || 0;
    const interest = Math.max(0, Math.min(cash, expectedInterest));
    setAllocatedInterest(interest);
    setAllocatedCapital(Math.max(0, cash - interest));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentAmount, matchType, expectedInterest]);

  // Multi-currency: Amount Settled and FX Rate are INDEPENDENT inputs. The four variables are
  //   1. Scheduled Amount (contract, fixed)  2. Amount Settled (foreign cash paid)
  //   3. FX Rate (for that settled amount)   4. Payment Amount equivalent = (2) × (3).
  // Only the equivalent is derived (the effect below); neither input recomputes the other.
  // The difference between the equivalent and the Scheduled Amount is the variance.
  // The rate + an on-open settled seed (= scheduled ÷ rate) are set once on open (rate-fetch
  // effect below) purely as a starting convenience.

  // T117: in multi-currency mode the contract-currency equivalent is DERIVED from the foreign
  // settlement amount and the dealt rate; keep paymentAmount in sync so all downstream variance
  // and allocation logic (which reads `cash`) operates on the contract-currency value unchanged.
  useEffect(() => {
    if (!isMultiCurrency || isReapportion || isView) return;
    const s = parseFloat(settlementAmount) || 0;
    const r = parseFloat(fxRate) || 0;
    setPaymentAmount(+(s * r).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementAmount, fxRate, isMultiCurrency]);

  // Auto-suggest the dealt rate from the live FX store when the dialog opens for a new
  // multi-currency match. User-overridable (it's an indicative rate, not the dealt rate).
  useEffect(() => {
    if (!open || !isMultiCurrency || isReapportion || isView) return;
    let cancelled = false;
    setFxError('');
    setFxLoading(true);
    apiGet(`/exchange-rates/current/${settlementCurrencyCode}/${currencyCode}`)
      .then((resp) => {
        if (cancelled) return;
        const r = parseFloat(resp?.rate?.rate);
        if (Number.isFinite(r) && r > 0) {
          setFxRate(String(r));
          // Seed Amount Settled = scheduled ÷ rate so the dialog opens on-schedule
          // (equivalent ≈ scheduled, variance ≈ 0). Reciprocal edits thereafter.
          if (expectedTotal > 0) setSettlementAmount(+(expectedTotal / r).toFixed(2));
        } else {
          setFxError(`No FX rate configured for ${settlementCurrencyCode} → ${currencyCode}. Enter the dealt rate manually.`);
        }
      })
      .catch(() => {
        if (!cancelled) setFxError(`Could not load an indicative ${settlementCurrencyCode} → ${currencyCode} rate. Enter the dealt rate manually.`);
      })
      .finally(() => { if (!cancelled) setFxLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMultiCurrency, settlementCurrencyCode, currencyCode]);

  const cash = parseFloat(paymentAmount) || 0;
  const variance = cash - expectedTotal;
  const hasVariance = Math.abs(variance) >= 0.01;

  const handleInterestChange = (value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    const clamped = Math.min(num, cash);
    setAllocatedInterest(clamped);
    setAllocatedCapital(Math.max(0, cash - clamped));
  };

  const handleCapitalChange = (value) => {
    const num = Math.max(0, parseFloat(value) || 0);
    const clamped = Math.min(num, cash);
    setAllocatedCapital(clamped);
    setAllocatedInterest(Math.max(0, cash - clamped));
  };

  const setInterestFirst = () => {
    const interest = Math.max(0, Math.min(cash, expectedInterest));
    setAllocatedInterest(interest);
    setAllocatedCapital(Math.max(0, cash - interest));
  };
  const setCapitalFirst = () => {
    const capital = Math.max(0, Math.min(cash, expectedCapital));
    setAllocatedCapital(capital);
    setAllocatedInterest(Math.max(0, cash - capital));
  };
  const setProRata = () => {
    if (expectedTotal <= 0) return;
    const ratio = expectedInterest / expectedTotal;
    const interest = +(cash * ratio).toFixed(2);
    setAllocatedInterest(interest);
    setAllocatedCapital(+(cash - interest).toFixed(2));
  };

  const allocationSumMatches = matchType !== 'combined'
    || Math.abs((allocatedInterest + allocatedCapital) - cash) < 0.01;
  const reasonRequired = hasVariance;
  const reasonOk = !reasonRequired || varianceReason.trim().length > 0;
  const referenceOk = paymentReference.trim().length > 0;
  // Proof is gated only when this is a new match (reapportion/view don't
  // re-upload). Skip the gate in those modes so existing matches without a
  // proof file remain editable.
  const proofOk = lockMetadata || !proofRequired || !!proofFile;
  // T117: a new multi-currency match needs a positive settlement amount and dealt rate.
  const settlementOk = !isMultiCurrency || lockMetadata
    || ((parseFloat(settlementAmount) || 0) > 0 && (parseFloat(fxRate) || 0) > 0);
  const isValid = cash > 0
    && allocationSumMatches
    && reasonOk
    && referenceOk
    && !!paymentDate
    && proofOk
    && settlementOk;

  const varianceColor = !hasVariance
    ? 'text.secondary'
    : (variance < 0 ? 'error.main' : 'success.main');
  const varianceLabel = !hasVariance
    ? 'On schedule'
    : `${variance < 0 ? 'Short' : 'Over'} ${currencyCode || ''} ${formatNumber(Math.abs(variance))}`;

  const titleByType = useMemo(() => {
    if (isView) {
      return {
        payment: 'Payment Details',
        combined: 'Combined Payment Details',
        wht: 'Withholding Tax Payment Details',
      };
    }
    if (isReapportion) {
      return {
        payment: 'Re-apportion Payment',
        combined: 'Re-apportion Combined Payment',
        wht: 'Re-apportion Withholding Tax Payment',
      };
    }
    return {
      payment: 'Match Payment',
      combined: 'Match Combined Payment',
      wht: 'Match Withholding Tax Payment',
    };
  }, [isReapportion, isView]);

  const handleConfirm = () => {
    const payload = {
      payment: {
        payment_amount: +cash.toFixed(2),
        payment_date: paymentDate,
        payment_reference: paymentReference.trim() || null,
        payment_method: paymentMethod,
        notes: notes.trim() || null,
        proof_file: proofFile,
        // T117: when multi-currency, surface the foreign cash + dealt rate so the caller can
        // pass them to the match endpoint. payment_amount stays the contract-currency equivalent.
        ...(isMultiCurrency ? {
          settlement_currency_code: settlementCurrencyCode,
          settlement_amount: +(parseFloat(settlementAmount) || 0).toFixed(2),
          fx_rate: parseFloat(fxRate) || 0,
        } : {}),
      },
      variance: hasVariance
        ? {
            amount: +variance.toFixed(2),
            reason: varianceReason.trim() || null,
          }
        : null,
    };
    if (matchType === 'combined') {
      payload.allocation = {
        allocated_interest: +allocatedInterest.toFixed(2),
        allocated_capital: +allocatedCapital.toFixed(2),
      };
    }
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titleByType[matchType] || 'Match Payment'}</DialogTitle>
      <DialogContent>
        {/* Header — contract / investor / scheduled */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="textSecondary">Contract</Typography>
              <Typography variant="body2" fontWeight="medium">{contractReference || '-'}</Typography>
              {userReference && (
                <Typography variant="caption" color="textSecondary">External Ref: {userReference}</Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="textSecondary">Investor</Typography>
              <Typography variant="body2" fontWeight="medium">{investorName || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: matchType === 'combined' ? 3 : 6 }}>
              <Typography variant="caption" color="textSecondary">Scheduled Date</Typography>
              <Typography variant="body2" fontWeight="medium">
                {scheduleRow?.payment_date ? format(new Date(scheduleRow.payment_date), 'dd MMM yyyy') : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: matchType === 'combined' ? 3 : 6 }}>
              <Typography variant="caption" color="textSecondary">Scheduled Amount</Typography>
              <Typography variant="body2" fontWeight="bold">
                {currencyCode} {formatNumber(expectedTotal)}
              </Typography>
            </Grid>
            {matchType === 'combined' && (
              <>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">Expected Interest</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(expectedInterest)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="textSecondary">Expected Capital</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(expectedCapital)}</Typography>
                </Grid>
              </>
            )}
            {matchType === 'wht' && whtContext && (
              <>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">Gross Interest</Typography>
                  <Typography variant="body2">{currencyCode} {formatNumber(whtContext.gross_interest)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">WHT Rate</Typography>
                  <Typography variant="body2">
                    {whtContext.wht_rate != null ? `${(parseFloat(whtContext.wht_rate) * (whtContext.wht_rate > 1 ? 1 : 100)).toFixed(2)}%` : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="textSecondary">WHT Due</Typography>
                  <Typography variant="body2" fontWeight="medium">{currencyCode} {formatNumber(whtContext.wht_amount)}</Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* T117: multi-currency settlement row — foreign cash + dealt rate. The contract-
            currency equivalent (Payment Amount) is derived and shown read-only beneath. */}
        {isMultiCurrency && (
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CurrencyInput
                label={`Amount ${matchType === 'wht' ? 'Paid' : (scheduleRow?.capital_amount > 0 && !(scheduleRow?.interest_amount > 0) ? 'Paid' : 'Settled')} (${settlementCurrencyCode})`}
                value={settlementAmount}
                onChange={(v) => setSettlementAmount(v)}
                required
                disabled={lockMetadata}
                helperText={lockMetadata ? 'Foreign cash settled (locked).' : `Cash actually moved in ${settlementCurrencyCode}`}
                sx={{ '& input': { textAlign: 'right' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={`FX Rate (${settlementCurrencyCode}/${currencyCode})`}
                value={fxRate}
                onChange={(e) => setFxRate(e.target.value.replace(/[^0-9.]/g, ''))}
                fullWidth
                required
                disabled={lockMetadata}
                error={!!fxError}
                helperText={
                  fxLoading ? 'Loading indicative rate…'
                    : (fxError || (lockMetadata ? 'Dealt rate (locked).' : `Dealt rate; pre-filled from system rate, override with the actual rate. 1 ${settlementCurrencyCode} = ? ${currencyCode}`))
                }
                inputProps={{ inputMode: 'decimal' }}
                sx={{ '& input': { textAlign: 'right' } }}
              />
            </Grid>
          </Grid>
        )}

        {/* Payment amount on its own row so the Allocation section can sit
            directly beneath it for Combined matches. In multi-currency mode this is the
            DERIVED contract-currency equivalent (settlement × rate) and is read-only. */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <CurrencyInput
              label={isMultiCurrency ? `Payment Amount (${currencyCode}, equivalent)` : 'Payment Amount'}
              value={paymentAmount}
              onChange={(v) => setPaymentAmount(v)}
              required
              disabled={lockMetadata || isMultiCurrency}
              helperText={
                isMultiCurrency
                  ? (() => {
                      const r = parseFloat(fxRate) || 0;
                      const s = parseFloat(settlementAmount) || 0;
                      return r > 0 && s > 0
                        ? `≈ ${currencyCode} ${formatNumber(s * r)} at ${settlementCurrencyCode}/${currencyCode} ${formatNumber(r, 6)}`
                        : `Equivalent in ${currencyCode}, computed from the settlement amount × rate`;
                    })()
                  : (isView ? 'Read-only.' : (isReapportion ? 'Cash amount is locked; reverse the match to change it.' : undefined))
              }
              sx={{ '& input': { textAlign: 'right' } }}
            />
          </Grid>
        </Grid>

        {/* Apportionment (Combined only) — directly under the cash amount */}
        {matchType === 'combined' && (
          <Box sx={{ mt: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Allocation</Typography>
              {!lockAllocation && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={setInterestFirst}>Interest first</Button>
                  <Button size="small" variant="outlined" onClick={setCapitalFirst}>Capital first</Button>
                  <Button size="small" variant="outlined" onClick={setProRata}>Pro-rata</Button>
                </Box>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CurrencyInput
                  label="Allocated to Interest"
                  value={allocatedInterest}
                  onChange={handleInterestChange}
                  disabled={lockAllocation}
                  sx={{ '& input': { textAlign: 'right' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <CurrencyInput
                  label="Allocated to Capital"
                  value={allocatedCapital}
                  onChange={handleCapitalChange}
                  disabled={lockAllocation}
                  sx={{ '& input': { textAlign: 'right' } }}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Remaining payment metadata — 3-column row: date / reference / method. */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Payment Date"
              type="date"
              fullWidth
              required
              disabled={lockMetadata}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Payment Reference"
              fullWidth
              required
              disabled={lockMetadata}
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} disabled={lockMetadata} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              disabled={lockMetadata}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
          {!lockMetadata && (
            <Grid size={{ xs: 12 }}>
              <ProofUploadField file={proofFile} onFileChange={setProofFile} label={proofLabel} />
            </Grid>
          )}
        </Grid>

        {/* Variance — always rendered to keep the dialog height stable. The
            reason field is disabled when cash matches expected. */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">Variance</Typography>
          <Chip
            size="small"
            label={varianceLabel}
            color={!hasVariance ? 'default' : (variance < 0 ? 'error' : 'success')}
            variant="outlined"
          />
        </Box>
        <TextField
          label="Variance reason"
          fullWidth
          multiline
          minRows={2}
          required={hasVariance}
          disabled={isView || !hasVariance}
          value={hasVariance ? varianceReason : ''}
          onChange={(e) => setVarianceReason(e.target.value)}
          placeholder={hasVariance ? '' : 'No variance — cash matches the scheduled amount.'}
          helperText={hasVariance ? 'Required when the cash received differs from the scheduled amount (e.g. counterparty schedule, fee deduction, FX shortfall).' : ' '}
        />
      </DialogContent>
      <DialogActions>
        {isView ? (
          <Button variant="contained" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm} disabled={!isValid || submitting}>
              {submitting
                ? (isReapportion ? 'Saving…' : 'Recording…')
                : (isReapportion ? 'Save Changes' : 'Confirm Match')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MatchDialog;
